import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import userContextSlice from '../../common/slices/userContextSlice';
import {
  isCurrentUserStaffOrSuperAdmin,
  getLeadershipTeams,
  getSelectedAssessmentPeriod,
} from '../../common/selectors/auth';
import GuidedExperienceModal from './components/GuidedExperienceModal';
import { guidedExperiences } from './guidedExperienceRegistry';
import { useUpdateGuidedExperienceState } from './api/guidedExperienceApi';

interface GuidedExperienceContextValue {
  currentExperienceId: string | null;
  currentStepIndex: number;
  currentStepCount: number;
  isOpen: boolean;
  startExperience: (id: string) => void;
  dismissExperience: () => void;
  goPrevious: () => void;
  goNext: () => void;
  completeExperience: () => Promise<void>;
  isSaving: boolean;
}

const GuidedExperienceContext = createContext<GuidedExperienceContextValue | undefined>(undefined);

export const GuidedExperienceProvider = ({ children }: { children: React.ReactNode }) => {
  const user = useSelector(userContextSlice.selectors.getUser);
  const isStaffOrSuperUser = useSelector(isCurrentUserStaffOrSuperAdmin);
  const selectedTeamId = useSelector(userContextSlice.selectors.getSelectedTeamId as any) as number | undefined;
  const leadershipTeams = useSelector(getLeadershipTeams);
  const selectedAssessmentPeriod = useSelector(getSelectedAssessmentPeriod);
  const [currentExperienceId, setCurrentExperienceId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [hasAutoStarted, setHasAutoStarted] = useState(false);
  const [pendingExperienceId, setPendingExperienceId] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { updateGuidedExperienceState, isLoading: isSaving } = useUpdateGuidedExperienceState();

  const selectedTeamAssignedToCurrentPeriod = useMemo(() => {
    if (selectedTeamId == null || !selectedAssessmentPeriod || !Array.isArray(leadershipTeams)) {
      return false;
    }

    return leadershipTeams.some(
      team =>
        team.id === selectedTeamId &&
        Array.isArray(team.assessments) &&
        team.assessments.includes(selectedAssessmentPeriod.id)
    );
  }, [selectedTeamId, selectedAssessmentPeriod, leadershipTeams]);

  const nextExperienceId = useMemo(() => {
    if (!user || isStaffOrSuperUser || !selectedTeamAssignedToCurrentPeriod) {
      return null;
    }

    return Object.values(guidedExperiences)
      .sort((a, b) => b.priority - a.priority)
      .find((experience) => !user.guidedExperienceState?.[experience.id]?.completed)
      ?.id ?? null;
  }, [isStaffOrSuperUser, user, selectedTeamAssignedToCurrentPeriod]);

  useEffect(() => {
    if (!user || hasAutoStarted || currentExperienceId !== null || !nextExperienceId) {
      return;
    }

    setCurrentExperienceId(nextExperienceId);
    setCurrentStepIndex(0);
    setIsOpen(true);
    setHasAutoStarted(true);
  }, [currentExperienceId, hasAutoStarted, nextExperienceId, user]);

  useEffect(() => {
    if (pendingExperienceId && selectedTeamAssignedToCurrentPeriod) {
      setCurrentExperienceId(pendingExperienceId);
      setCurrentStepIndex(0);
      setIsOpen(true);
      setPendingExperienceId(null);
    }
  }, [pendingExperienceId, selectedTeamAssignedToCurrentPeriod]);

  const experience = useMemo(
    () => (currentExperienceId ? guidedExperiences[currentExperienceId] : undefined),
    [currentExperienceId]
  );

  const startExperience = useCallback((id: string) => {
    if (!guidedExperiences[id]) {
      console.warn(`Guided experience '${id}' is not registered.`);
      return;
    }

    if (id === 'welcome-tour' && !selectedTeamAssignedToCurrentPeriod) {
      setPendingExperienceId(id);
      return;
    }

    setPendingExperienceId(null);
    setCurrentExperienceId(id);
    setCurrentStepIndex(0);
    setIsOpen(true);
  }, [selectedTeamAssignedToCurrentPeriod]);

  const dismissExperience = useCallback(() => {
    setIsOpen(false);
    setCurrentExperienceId(null);
    setCurrentStepIndex(0);
    setHasAutoStarted(true);
  }, []);

  const completeExperience = useCallback(async () => {
    if (!currentExperienceId) {
      return;
    }

    const currentState = user?.guidedExperienceState ?? {};
    const nextState = {
      ...currentState,
      [currentExperienceId]: {
        completed: true,
        completedAt: new Date().toISOString(),
      },
    };

    try {
      await updateGuidedExperienceState(nextState);
    } catch (error) {
      console.error('Failed to persist guided experience completion', error);
    } finally {
      setIsOpen(false);
      setCurrentExperienceId(null);
      setCurrentStepIndex(0);
      setHasAutoStarted(true);

      if (currentExperienceId === 'welcome-tour') {
        const isMobileDevice = isMobile || location.pathname.includes('/app/mobile');
        const redirectPath = isMobileDevice
          ? '/app/mobile/focus-areas'
          : '/app/culture-hub/interventions';
        navigate(redirectPath, { replace: true });
      }
    }
  }, [currentExperienceId, location.pathname, navigate, updateGuidedExperienceState, user?.guidedExperienceState]);

  const goPrevious = useCallback(() => {
    setCurrentStepIndex((currentIndex) => Math.max(currentIndex - 1, 0));
  }, []);

  const goNext = useCallback(() => {
    if (!experience) {
      return;
    }

    setCurrentStepIndex((currentIndex) =>
      currentIndex + 1 < experience.steps.length ? currentIndex + 1 : currentIndex
    );
  }, [experience]);

  const value = useMemo(
    () => ({
      currentExperienceId,
      currentStepIndex,
      currentStepCount: experience?.steps.length ?? 0,
      isOpen,
      startExperience,
      dismissExperience,
      goPrevious,
      goNext,
      completeExperience,
      isSaving,
    }),
    [currentExperienceId, currentStepIndex, experience?.steps.length, isOpen, startExperience, dismissExperience, goPrevious, goNext, completeExperience, isSaving]
  );

  return (
    <GuidedExperienceContext.Provider value={value}>
      {children}
      {experience ? (
        <GuidedExperienceModal
          open={isOpen}
          experience={experience}
          stepIndex={currentStepIndex}
          onPrevious={goPrevious}
          onNext={goNext}
          onComplete={completeExperience}
          onClose={dismissExperience}
          isSaving={isSaving}
        />
      ) : null}
    </GuidedExperienceContext.Provider>
  );
};

export const useGuidedExperience = () => {
  const context = useContext(GuidedExperienceContext);
  if (!context) {
    throw new Error('useGuidedExperience must be used within a GuidedExperienceProvider');
  }
  return context;
};
